import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import usePermissions from '../../hooks/usePermissions';
import { PERMISSIONS } from '../../config/permissions';
import PasswordInput from '../../components/common/PasswordInput';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const NIGERIAN_STATES = [
  'Abia','Adamawa','Akwa Ibom','Anambra','Bauchi','Bayelsa','Benue','Borno',
  'Cross River','Delta','Ebonyi','Edo','Ekiti','Enugu','FCT','Gombe','Imo',
  'Jigawa','Kaduna','Kano','Katsina','Kebbi','Kogi','Kwara','Lagos','Nasarawa',
  'Niger','Ogun','Ondo','Osun','Oyo','Plateau','Rivers','Sokoto','Taraba',
  'Yobe','Zamfara',
];

export default function AdminMembers() {
  const { canEdit, canAdd, canDelete } = usePermissions();
  const [members, setMembers] = useState([]);
  const [pendingMembers, setPendingMembers] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Modal state
  const [viewMember, setViewMember] = useState(null);
  const [editMember, setEditMember] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editPhoto, setEditPhoto] = useState(null);
  const [editPhotoPreview, setEditPhotoPreview] = useState(null);
  const [deletePhoto, setDeletePhoto] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [deleteMember, setDeleteMember] = useState(null);
  const [declineTarget, setDeclineTarget] = useState(null);
  const [actingId, setActingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [stats, setStats] = useState({ fellow: 0, corporate: 0, associate: 0, graduate: 0, student: 0, technician: 0, probationer: 0 });

  // Create Member modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    email: '', first_name: '', last_name: '', password: '',
    phone_number: '', gender: '', date_of_birth: '', address: '', city: '', state: '', state_of_origin: '',
    membership_category: 'graduate', membership_status: 'active', membership_number: '',
    employment_category: '',
    specialization: '', firm_name: '', firm_address: '', surcon_number: '', shortcode: '',
    academic_qualification: '', year_registered: '', date_joined_branch: '',
    registered_with_nis_hq: false, bio: '',
  });
  const [createPhoto, setCreatePhoto] = useState(null);
  const [createPhotoPreview, setCreatePhotoPreview] = useState(null);

  const fetchStats = async () => {
    try {
      const res = await api.get('/accounts/admin/members/stats/');
      setStats(res.data);
    } catch { /* ignore */ }
  };

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page_size', '1000');
      if (statusFilter) params.append('membership_status', statusFilter);
      if (categoryFilter) params.append('membership_category', categoryFilter);
      const res = await api.get(`/accounts/admin/members/?${params}`);
      setMembers(res.data.results || res.data);
    } finally {
      setLoading(false);
    }
  };

  // Pending applications are fetched separately so they always show at the
  // top of the page, regardless of the status/category filters below.
  const fetchPending = async () => {
    try {
      const res = await api.get('/accounts/admin/members/?membership_status=pending&page_size=1000');
      setPendingMembers(res.data.results || res.data);
    } catch { /* ignore */ }
  };

  useEffect(() => { fetchMembers(); fetchPending(); fetchStats(); }, []);
  useEffect(() => { fetchMembers(); }, [statusFilter, categoryFilter]);

  // Client-side search filtering — supports full-name search (e.g. "Yusuf Obaje Yunusa")
  const matchesSearch = (m) => {
    if (!search) return true;
    const q = search.toLowerCase().trim();
    // Build a combined string with all name permutations so any order matches
    const surname = (m.surname || '').toLowerCase();
    const otherNames = (m.other_names || '').toLowerCase();
    const combined = `${surname} ${otherNames} ${otherNames} ${surname} ${m.full_name || ''} ${m.email || ''} ${m.membership_number || ''} ${m.phone_number || ''}`.toLowerCase();
    // Match if the whole query appears as-is, OR every word appears somewhere
    if (combined.includes(q)) return true;
    const words = q.split(/\s+/).filter(Boolean);
    return words.length > 1 && words.every(w => combined.includes(w));
  };

  // Pending applicants live in the panel above the table — they only "join
  // the rest of the members" once approved. But when the admin is searching,
  // the table shows matches from ALL members (pending included) so search
  // covers everyone in one result list.
  const filteredMembers = members.filter(
    (m) => (search ? true : m.membership_status !== 'pending') && matchesSearch(m)
  );
  const filteredPending = pendingMembers.filter(matchesSearch);

  // Reset to page 1 when filtered list or page size changes
  useEffect(() => { setCurrentPage(1); }, [search, members, pageSize]);

  const totalPages = pageSize === 'all' ? 1 : Math.ceil(filteredMembers.length / pageSize);
  const paginatedMembers = pageSize === 'all'
    ? filteredMembers
    : filteredMembers.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const approveMember = async (id) => {
    setActingId(id);
    try {
      await api.post(`/accounts/admin/members/${id}/approve/`);
      toast.success('Application approved — the member has been notified by email.');
      fetchMembers();
      fetchPending();
      fetchStats();
    } catch {
      toast.error('Failed to approve.');
    } finally {
      setActingId(null);
    }
  };

  const handleDecline = async () => {
    if (!declineTarget) return;
    setSaving(true);
    try {
      await api.post(`/accounts/admin/members/${declineTarget.id}/decline/`);
      toast.success('Application declined — the applicant has been notified by email.');
      setDeclineTarget(null);
      fetchMembers();
      fetchPending();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to decline application.');
    } finally {
      setSaving(false);
    }
  };

  const openViewModal = async (id) => {
    try {
      const res = await api.get(`/accounts/admin/members/${id}/`);
      setViewMember(res.data);
    } catch {
      toast.error('Failed to load member details.');
    }
  };

  const openEditModal = async (id) => {
    try {
      const res = await api.get(`/accounts/admin/members/${id}/`);
      setEditMember(res.data);
      setEditPhoto(null);
      setEditPhotoPreview(res.data.photo || null);
      setDeletePhoto(false);
      setNewPassword('');
      setEditForm({
        first_name: res.data.user?.first_name || '',
        last_name: res.data.user?.last_name || '',
        email: res.data.user?.email || '',
        membership_number: res.data.membership_number || '',
        membership_category: res.data.membership_category || '',
        membership_status: res.data.membership_status || '',
        gender: res.data.gender || '',
        date_of_birth: res.data.date_of_birth || '',
        phone_number: res.data.phone_number || '',
        alt_phone_number: res.data.alt_phone_number || '',
        address: res.data.address || '',
        city: res.data.city || '',
        state: res.data.state || '',
        state_of_origin: res.data.state_of_origin || '',
        firm_name: res.data.firm_name || '',
        firm_address: res.data.firm_address || '',
        employment_category: res.data.employment_category || '',
        specialization: res.data.specialization || '',
        surcon_number: res.data.surcon_number || '',
        shortcode: res.data.shortcode || '',
        bio: res.data.bio || '',
        academic_qualification: res.data.academic_qualification || '',
        year_registered: res.data.year_registered || '',
        date_joined_branch: res.data.date_joined_branch || '',
        registered_with_nis_hq: res.data.registered_with_nis_hq || false,
      });
    } catch {
      toast.error('Failed to load member details.');
    }
  };

  const handleEditPhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Photo must be less than 2MB');
        return;
      }
      setEditPhoto(file);
      setEditPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleEditSave = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(editForm).forEach(([key, val]) => {
        if (val !== '' && val !== null && val !== undefined) {
          fd.append(key, val);
        }
      });
      // Always send the two name fields even if empty so the user can
      // legitimately blank them (rare, but supported).
      fd.set('first_name', editForm.first_name || '');
      fd.set('last_name', editForm.last_name || '');
      fd.set('employment_category', editForm.employment_category || '');
      // Email: only send when the admin has actually entered a value AND
      // it differs from the loaded one. Otherwise omit the field entirely.
      // Sending an empty string used to silently wipe `user.email`, which
      // broke login + forgot-password for that member forever after.
      const trimmedEmail = (editForm.email || '').trim();
      const currentEmail = (editMember?.user?.email || '').trim();
      if (trimmedEmail && trimmedEmail.toLowerCase() !== currentEmail.toLowerCase()) {
        fd.set('email', trimmedEmail);
      } else {
        fd.delete('email');
      }
      if (editPhoto) fd.append('photo', editPhoto);
      if (deletePhoto) fd.append('photo', '');
      await api.patch(`/accounts/admin/members/${editMember.id}/`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Member updated successfully!');
      setEditMember(null);
      setEditPhoto(null);
      setEditPhotoPreview(null);
      setDeletePhoto(false);
      setNewPassword('');
      fetchMembers();
    } catch (err) {
      const data = err.response?.data;
      if (data?.email) {
        toast.error(Array.isArray(data.email) ? data.email[0] : data.email);
      } else {
        toast.error('Failed to update member.');
      }
    } finally {
      setSaving(false);
    }
  };

  const downloadMemberPDF = async (member) => {
    const doc = new jsPDF();
    const green = [11, 93, 30];

    // Header bar
    doc.setFillColor(...green);
    doc.rect(0, 0, 210, 36, 'F');

    // Load logo into header
    try {
      const logoImg = new Image();
      await new Promise((resolve, reject) => {
        logoImg.onload = resolve;
        logoImg.onerror = reject;
        logoImg.src = '/NIS ABJ LOGO.jpg';
      });
      const logoCanvas = document.createElement('canvas');
      logoCanvas.width = logoImg.width;
      logoCanvas.height = logoImg.height;
      logoCanvas.getContext('2d').drawImage(logoImg, 0, 0);
      const logoData = logoCanvas.toDataURL('image/jpeg', 0.9);
      doc.addImage(logoData, 'JPEG', 14, 4, 28, 28);
    } catch {
      // Logo failed to load — continue without it
    }

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('NIS Abuja (FCT) Branch', 120, 16, { align: 'center' });
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text('Member Profile', 120, 25, { align: 'center' });

    // Load member photo if available
    let photoLoaded = false;
    if (member.photo) {
      try {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = member.photo;
        });
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const imgData = canvas.toDataURL('image/jpeg', 0.85);
        // Preserve aspect ratio within a 25mm box
        const maxW = 25;
        const maxH = 30;
        const ratio = Math.min(maxW / img.width, maxH / img.height);
        const imgW = img.width * ratio;
        const imgH = img.height * ratio;
        doc.addImage(imgData, 'JPEG', 14, 42, imgW, imgH);
        photoLoaded = true;
      } catch {
        // Photo failed to load — skip it
      }
    }

    // Member name & status (shift right if photo is present)
    const textX = photoLoaded ? 44 : 14;
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(member.full_name || '—', textX, 52);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text(member.user?.email || '', textX, 59);
    doc.text(`Status: ${(member.membership_status || '—').toUpperCase()}   |   Category: ${(member.membership_category || '—').toUpperCase()}`, textX, 65);

    // Profile table
    const rows = [
      ['Membership Number', member.membership_number],
      ['Shortcode', member.shortcode],
      ['Gender', member.gender],
      ['Date of Birth', member.date_of_birth],
      ['Phone Number', member.phone_number],
      ['Alt Phone', member.alt_phone_number],
      ['State of Origin', member.state_of_origin],
      ['Address', member.address],
      ['City', member.city],
      ['State', member.state],
      ['Firm Name', member.firm_name],
      ['Firm Address', member.firm_address],
      ['Employment Category', member.employment_category === 'private_sector'
        ? 'Private Sector'
        : member.employment_category === 'public_sector' ? 'Public Sector' : null],
      ['Specialization', member.specialization],
      ['SURCON Number', member.surcon_number],
      ['Year Registered', member.year_registered],
      ['Academic Qualification', member.academic_qualification],
      ['Registered with NIS HQ', member.registered_with_nis_hq ? 'Yes' : 'No'],
      ['Date Joined Branch', member.date_joined_branch],
      ['Dues Status', member.dues_status],
      ['Bio', member.bio],
    ].map(([label, val]) => [label, val || '—']);

    autoTable(doc, {
      startY: 72,
      head: [['Field', 'Details']],
      body: rows,
      theme: 'striped',
      headStyles: { fillColor: green, textColor: [255, 255, 255], fontStyle: 'bold' },
      styles: { fontSize: 10, cellPadding: 4 },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 55 } },
    });

    // Footer
    const pageHeight = doc.internal.pageSize.getHeight();
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(`Generated on ${new Date().toLocaleDateString()}`, 14, pageHeight - 10);
    doc.text('Nigerian Institution of Surveyors — FCT Branch', 196, pageHeight - 10, { align: 'right' });

    const safeName = (member.full_name || 'member').replace(/\s+/g, '_');
    doc.save(`${safeName}_profile.pdf`);
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await api.delete(`/accounts/admin/members/${deleteMember.id}/`);
      toast.success('Member deleted successfully.');
      setDeleteMember(null);
      setSelectedIds((prev) => { const next = new Set(prev); next.delete(deleteMember.id); return next; });
      fetchMembers();
      fetchStats();
    } catch {
      toast.error('Failed to delete member.');
    } finally {
      setSaving(false);
    }
  };

  const handleBulkDelete = async () => {
    setSaving(true);
    let deleted = 0;
    let failed = 0;
    for (const id of selectedIds) {
      try {
        await api.delete(`/accounts/admin/members/${id}/`);
        deleted++;
      } catch {
        failed++;
      }
    }
    setSaving(false);
    setBulkDeleteConfirm(false);
    setSelectedIds(new Set());
    if (deleted) toast.success(`${deleted} member${deleted > 1 ? 's' : ''} deleted.`);
    if (failed) toast.error(`${failed} member${failed > 1 ? 's' : ''} failed to delete.`);
    fetchMembers();
    fetchStats();
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const pageIds = paginatedMembers.map((m) => m.id);
    const allSelected = pageIds.every((id) => selectedIds.has(id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      pageIds.forEach((id) => allSelected ? next.delete(id) : next.add(id));
      return next;
    });
  };

  const resetCreateForm = () => {
    setCreateForm({
      email: '', first_name: '', last_name: '', password: '',
      phone_number: '', gender: '', date_of_birth: '', address: '', city: '', state: '', state_of_origin: '',
      membership_category: 'graduate', membership_status: 'active', membership_number: '',
      employment_category: '',
      specialization: '', firm_name: '', firm_address: '', surcon_number: '', shortcode: '',
      academic_qualification: '', year_registered: '', date_joined_branch: '',
      registered_with_nis_hq: false, bio: '',
    });
    setCreatePhoto(null);
    setCreatePhotoPreview(null);
    setShowCreateModal(false);
  };

  const handleCreateMember = async () => {
    if (!createForm.email || !createForm.first_name || !createForm.last_name) {
      toast.error('Email, first name and last name are required.');
      return;
    }
    if (createForm.password.length < 8) {
      toast.error('Password must be at least 8 characters.');
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(createForm).forEach(([key, val]) => {
        if (val !== '' && val !== null && val !== undefined) {
          fd.append(key, val);
        }
      });
      if (createPhoto) fd.append('photo', createPhoto);
      await api.post('/accounts/admin/members/create/', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Member created successfully!');
      resetCreateForm();
      fetchMembers();
      fetchStats();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to create member.';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Members Management</h1>
        {canAdd(PERMISSIONS.ADD_MEMBERS) && (
          <button onClick={() => setShowCreateModal(true)}
            className="bg-[#0B5D1E] text-white px-6 py-2 rounded-lg text-sm font-semibold hover:bg-[#062E10] transition inline-flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Create Member
          </button>
        )}
      </div>

      {/* Category Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Fellow', key: 'fellow', color: 'bg-purple-600', icon: 'M12 14l9-5-9-5-9 5 9 5z' },
          { label: 'Corporate', key: 'corporate', color: 'bg-blue-600', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' },
          { label: 'Associate', key: 'associate', color: 'bg-green-600', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z' },
          { label: 'Probationer', key: 'probationer', color: 'bg-orange-500', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' },
        ].map(({ label, key, color, icon }) => (
            <div key={key} className="bg-white rounded-xl shadow-sm border p-4 flex items-center gap-4">
              <div className={`${color} w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0`}>
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon} />
                </svg>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats[key] ?? 0}</p>
                <p className="text-xs text-gray-500 font-medium">{label}</p>
              </div>
            </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border p-4 mb-6">
        <form onSubmit={(e) => { e.preventDefault(); fetchMembers(); fetchPending(); }} className="flex flex-col md:flex-row gap-3">
          <input type="text" placeholder="Search by name, email, membership number..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none" />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none">
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="suspended">Suspended</option>
          </select>
          <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none">
            <option value="">All Categories</option>
            <option value="fellow">Fellow</option>
            <option value="corporate">Corporate</option>
            <option value="associate">Associate</option>
            <option value="graduate">Graduate</option>
            <option value="student">Student</option>
            <option value="technician">Technician</option>
            <option value="probationer">Probationer</option>
          </select>
          <button type="submit"
            className="bg-[#0B5D1E] text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#062E10] transition">
            Search
          </button>
        </form>
      </div>

      {/* Pending Membership Applications */}
      {filteredPending.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-yellow-300 mb-6 overflow-hidden">
          <div className="bg-yellow-50 px-6 py-3 border-b border-yellow-200 flex items-center gap-2">
            <svg className="w-5 h-5 text-yellow-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className="text-sm font-bold text-yellow-800 uppercase tracking-wide">Pending Membership Applications</h2>
            <span className="ml-auto text-xs bg-yellow-200 text-yellow-800 font-bold px-2.5 py-1 rounded-full">
              {filteredPending.length}
            </span>
          </div>
          <ul className="divide-y">
            {filteredPending.map((m) => (
              <li key={m.id} className="px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-3 hover:bg-gray-50">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {m.photo ? (
                    <img src={m.photo} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-[#0B5D1E] text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
                      {(m.surname || m.full_name || '?').charAt(0)}
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{m.full_name || `${m.surname || ''} ${m.other_names || ''}`.trim()}</p>
                    <p className="text-xs text-gray-500 truncate">{m.email}</p>
                  </div>
                  <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600 font-semibold capitalize hidden md:inline flex-shrink-0">
                    {m.membership_category}
                  </span>
                  {m.created_at && (
                    <span className="text-xs text-gray-400 hidden lg:inline flex-shrink-0">
                      Applied {new Date(m.created_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button onClick={() => openViewModal(m.id)}
                    className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 transition inline-flex items-center gap-1"
                    title="View Application">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    View
                  </button>
                  {canEdit(PERMISSIONS.EDIT_MEMBERS) && (
                    <>
                      <button onClick={() => approveMember(m.id)} disabled={actingId === m.id}
                        className="text-xs bg-green-600 text-white px-3 py-1.5 rounded hover:bg-green-700 transition inline-flex items-center gap-1 disabled:opacity-50"
                        title="Approve Application">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {actingId === m.id ? 'Approving...' : 'Approve'}
                      </button>
                      <button onClick={() => setDeclineTarget(m)} disabled={actingId === m.id}
                        className="text-xs bg-red-600 text-white px-3 py-1.5 rounded hover:bg-red-700 transition inline-flex items-center gap-1 disabled:opacity-50"
                        title="Decline Application">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Decline
                      </button>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 flex items-center justify-between">
          <span className="text-sm text-red-700 font-medium">
            {selectedIds.size} member{selectedIds.size > 1 ? 's' : ''} selected
          </span>
          <div className="flex items-center gap-2">
            <button onClick={() => setSelectedIds(new Set())}
              className="text-sm px-3 py-1.5 bg-white border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition">
              Clear Selection
            </button>
            {canDelete(PERMISSIONS.DELETE_MEMBERS) && (
              <button onClick={() => setBulkDeleteConfirm(true)}
                className="text-sm px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition inline-flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Delete Selected
              </button>
            )}
          </div>
        </div>
      )}

      {/* Members Table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-4 py-3">
                <input type="checkbox"
                  checked={paginatedMembers.length > 0 && paginatedMembers.every((m) => selectedIds.has(m.id))}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 rounded border-gray-300 text-[#0B5D1E] focus:ring-[#0B5D1E] cursor-pointer" />
              </th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">S/N</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Surname</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Other Names</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">ID</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Category</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Dues</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {loading ? (
              <tr><td colSpan="9" className="text-center py-8 text-gray-500">Loading...</td></tr>
            ) : filteredMembers.length === 0 ? (
              <tr><td colSpan="9" className="text-center py-8 text-gray-500">No members found.</td></tr>
            ) : paginatedMembers.map((m, idx) => (
              <tr key={m.id} className={`hover:bg-gray-50 ${selectedIds.has(m.id) ? 'bg-blue-50' : ''}`}>
                <td className="px-4 py-4">
                  <input type="checkbox"
                    checked={selectedIds.has(m.id)}
                    onChange={() => toggleSelect(m.id)}
                    className="w-4 h-4 rounded border-gray-300 text-[#0B5D1E] focus:ring-[#0B5D1E] cursor-pointer" />
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">{(pageSize === 'all' ? 0 : (currentPage - 1) * pageSize) + idx + 1}</td>
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-3">
                    {m.photo ? (
                      <img src={m.photo} alt="" className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-[#0B5D1E] text-white flex items-center justify-center font-bold text-sm">
                        {(m.surname || '').charAt(0)}
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-sm">{m.surname}</p>
                      <p className="text-xs text-gray-500">{m.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm">{m.other_names}</td>
                <td className="px-6 py-4 text-sm font-mono">{m.membership_number || '—'}</td>
                <td className="px-6 py-4 text-sm capitalize">{m.membership_category}</td>
                <td className="px-6 py-4">
                  <span className={`text-xs px-2 py-1 rounded-full font-semibold ${
                    m.membership_status === 'active' ? 'bg-green-100 text-green-700' :
                    m.membership_status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>{m.membership_status}</span>
                </td>
                <td className="px-6 py-4">
                  <span className={`text-xs px-2 py-1 rounded-full font-semibold ${
                    m.dues_status === 'credit' ? 'bg-green-100 text-green-700' :
                    m.dues_status === 'debit' ? 'bg-red-100 text-red-700' :
                    'bg-yellow-100 text-yellow-700'
                  }`}>{m.dues_status}</span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <button onClick={() => openViewModal(m.id)}
                      className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 transition inline-flex items-center gap-1"
                      title="View Details">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      View
                    </button>
                    {canEdit(PERMISSIONS.EDIT_MEMBERS) && (
                      <button onClick={() => openEditModal(m.id)}
                        className="text-xs bg-yellow-500 text-white px-3 py-1 rounded hover:bg-yellow-600 transition inline-flex items-center gap-1"
                        title="Edit Member">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Edit
                      </button>
                    )}
                    {canDelete(PERMISSIONS.DELETE_MEMBERS) && (
                      <button onClick={() => setDeleteMember(m)}
                        className="text-xs bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 transition inline-flex items-center gap-1"
                        title="Delete Member">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Delete
                      </button>
                    )}
                    <button onClick={async () => {
                        try {
                          const res = await api.get(`/accounts/admin/members/${m.id}/`);
                          await downloadMemberPDF(res.data);
                        } catch { toast.error('Failed to download PDF.'); }
                      }}
                      className="text-xs bg-purple-600 text-white px-3 py-1 rounded hover:bg-purple-700 transition inline-flex items-center gap-1"
                      title="Download PDF">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      PDF
                    </button>
                    {canEdit(PERMISSIONS.EDIT_MEMBERS) && m.membership_status !== 'active' && (
                      <button onClick={() => approveMember(m.id)} disabled={actingId === m.id}
                        className="text-xs bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 transition inline-flex items-center gap-1 disabled:opacity-50"
                        title="Approve Member">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {actingId === m.id ? 'Approving...' : 'Approve'}
                      </button>
                    )}
                    {canEdit(PERMISSIONS.EDIT_MEMBERS) && m.membership_status === 'pending' && (
                      <button onClick={() => setDeclineTarget(m)} disabled={actingId === m.id}
                        className="text-xs bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 transition inline-flex items-center gap-1 disabled:opacity-50"
                        title="Decline Application">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Decline
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination Controls */}
        {filteredMembers.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between px-6 py-4 border-t bg-gray-50 gap-3">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>Show</span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(e.target.value === 'all' ? 'all' : Number(e.target.value))}
                className="px-2 py-1 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value="all">All</option>
              </select>
              <span>of {filteredMembers.length} records</span>
            </div>

            {pageSize !== 'all' && totalPages > 1 && (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="px-2 py-1 text-sm border rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  &laquo;
                </button>
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-2 py-1 text-sm border rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  &lsaquo;
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                  .reduce((acc, p, idx, arr) => {
                    if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, idx) =>
                    p === '...' ? (
                      <span key={`dot-${idx}`} className="px-2 py-1 text-sm text-gray-400">...</span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setCurrentPage(p)}
                        className={`px-3 py-1 text-sm border rounded ${
                          currentPage === p
                            ? 'bg-[#0B5D1E] text-white border-[#0B5D1E]'
                            : 'hover:bg-gray-100'
                        }`}
                      >
                        {p}
                      </button>
                    )
                  )}
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-2 py-1 text-sm border rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  &rsaquo;
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="px-2 py-1 text-sm border rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  &raquo;
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ——— View Member Modal ——— */}
      {viewMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-bold text-gray-900">Member Details</h2>
              <button onClick={() => setViewMember(null)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
            </div>
            <div className="p-6">
              <div className="flex items-center gap-4 mb-6">
                {viewMember.photo ? (
                  <img src={viewMember.photo} alt="" className="w-20 h-20 rounded-full object-cover" />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-[#0B5D1E] text-white flex items-center justify-center font-bold text-2xl">
                    {viewMember.full_name?.charAt(0)}
                  </div>
                )}
                <div>
                  <h3 className="text-lg font-semibold">{viewMember.full_name}</h3>
                  <p className="text-sm text-gray-500">{viewMember.user?.email}</p>
                  <span className={`text-xs px-2 py-1 rounded-full font-semibold ${
                    viewMember.membership_status === 'active' ? 'bg-green-100 text-green-700' :
                    viewMember.membership_status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>{viewMember.membership_status}</span>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                {[
                  ['Membership Number', viewMember.membership_number],
                  ['Category', viewMember.membership_category],
                  ['Shortcode', viewMember.shortcode],
                  ['Gender', viewMember.gender],
                  ['Date of Birth', viewMember.date_of_birth],
                  ['Phone', viewMember.phone_number],
                  ['Alt Phone', viewMember.alt_phone_number],
                  ['Address', viewMember.address],
                  ['City', viewMember.city],
                  ['State', viewMember.state],
                  ['State of Origin', viewMember.state_of_origin],
                  ['Firm Name', viewMember.firm_name],
                  ['Firm Address', viewMember.firm_address],
                  ['Employment Category', viewMember.employment_category === 'private_sector'
                    ? 'Private Sector'
                    : viewMember.employment_category === 'public_sector' ? 'Public Sector' : null],
                  ['Specialization', viewMember.specialization],
                  ['SURCON Number', viewMember.surcon_number],
                  ['Year Registered', viewMember.year_registered],
                  ['Academic Qualification', viewMember.academic_qualification],
                  ['Registered with NIS HQ', viewMember.registered_with_nis_hq ? 'Yes' : 'No'],
                  ['Date Joined Branch', viewMember.date_joined_branch],
                  ['Dues Status', viewMember.dues_status],
                  ['Total CPD Points', viewMember.total_cpd_points],
                  ['Member Since', viewMember.created_at ? new Date(viewMember.created_at).toLocaleDateString() : null],
                  ['Last Updated', viewMember.updated_at ? new Date(viewMember.updated_at).toLocaleDateString() : null],
                ].map(([label, value]) => (
                  <div key={label}>
                    <p className="text-gray-500 text-xs">{label}</p>
                    <p className="font-medium capitalize">{value || '—'}</p>
                  </div>
                ))}
              </div>
              {viewMember.bio && (
                <div className="mt-4">
                  <p className="text-gray-500 text-xs">Bio</p>
                  <p className="font-medium text-sm mt-1">{viewMember.bio}</p>
                </div>
              )}
            </div>
            <div className="p-6 border-t flex justify-end gap-3">
              <button onClick={() => downloadMemberPDF(viewMember)}
                className="px-4 py-2 bg-[#0B5D1E] text-white rounded-lg hover:bg-[#062E10] transition inline-flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download PDF
              </button>
              <button onClick={() => setViewMember(null)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ——— Edit Member Modal ——— */}
      {editMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-bold text-gray-900">Edit Member — {editMember.full_name}</h2>
              <button onClick={() => setEditMember(null)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
            </div>
            <div className="p-6 space-y-6">

              {/* Photo Upload */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-2">Passport Photograph</label>
                <div className="flex items-center gap-4">
                  {editPhotoPreview ? (
                    <div className="relative">
                      <img src={editPhotoPreview} alt="Preview" className="w-20 h-20 rounded-full object-cover border-2 border-gray-200" />
                      <button type="button" onClick={() => { setEditPhoto(null); setEditPhotoPreview(null); setDeletePhoto(true); }}
                        className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition shadow-sm"
                        title="Remove photo">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center text-gray-400">
                      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                  )}
                  <div>
                    <label className="cursor-pointer inline-block px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition text-sm font-medium">
                      Choose Photo
                      <input type="file" accept="image/*" onChange={handleEditPhotoChange} className="hidden" />
                    </label>
                    <p className="text-xs text-gray-400 mt-1">Max 2MB — JPG, PNG</p>
                  </div>
                </div>
              </div>

              {/* Name & Email */}
              <div>
                <h3 className="text-sm font-bold text-gray-700 mb-3 border-b pb-1">Name & Email</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">First Name</label>
                    <input type="text" value={editForm.first_name}
                      onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Last Name</label>
                    <input type="text" value={editForm.last_name}
                      onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Email</label>
                    <input type="email" value={editForm.email}
                      onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                    <p className="text-xs text-amber-600 mt-1">Changing the email will update the member's login email.</p>
                  </div>
                </div>
              </div>

              {/* Membership Info */}
              <div>
                <h3 className="text-sm font-bold text-gray-700 mb-3 border-b pb-1">Membership Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Membership Number</label>
                    <input type="text" value={editForm.membership_number}
                      onChange={(e) => setEditForm({ ...editForm, membership_number: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Category</label>
                    <select value={editForm.membership_category}
                      onChange={(e) => setEditForm({ ...editForm, membership_category: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm">
                      <option value="fellow">Fellow (FNIS)</option>
                      <option value="corporate">Corporate Member (MNIS)</option>
                      <option value="associate">Associate Member</option>
                      <option value="graduate">Graduate Member</option>
                      <option value="student">Student Member</option>
                      <option value="technician">Technician Member</option>
                      <option value="probationer">Probationer</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Status</label>
                    <select value={editForm.membership_status}
                      onChange={(e) => setEditForm({ ...editForm, membership_status: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm">
                      <option value="active">Active</option>
                      <option value="pending">Pending</option>
                      <option value="inactive">Inactive</option>
                      <option value="suspended">Suspended</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Shortcode</label>
                    <input type="text" value={editForm.shortcode}
                      onChange={(e) => setEditForm({ ...editForm, shortcode: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                </div>
              </div>

              {/* Personal Details */}
              <div>
                <h3 className="text-sm font-bold text-gray-700 mb-3 border-b pb-1">Personal Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Gender</label>
                    <select value={editForm.gender}
                      onChange={(e) => setEditForm({ ...editForm, gender: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm">
                      <option value="">—</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Date of Birth</label>
                    <input type="date" value={editForm.date_of_birth}
                      onChange={(e) => setEditForm({ ...editForm, date_of_birth: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Phone Number</label>
                    <input type="tel" value={editForm.phone_number}
                      onChange={(e) => setEditForm({ ...editForm, phone_number: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Alt Phone Number</label>
                    <input type="tel" value={editForm.alt_phone_number}
                      onChange={(e) => setEditForm({ ...editForm, alt_phone_number: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">State of Origin</label>
                    <select value={editForm.state_of_origin}
                      onChange={(e) => setEditForm({ ...editForm, state_of_origin: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm">
                      <option value="">Select State</option>
                      {NIGERIAN_STATES.map((st) => (
                        <option key={st} value={st}>{st}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">City</label>
                    <input type="text" value={editForm.city}
                      onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">State</label>
                    <input type="text" value={editForm.state}
                      onChange={(e) => setEditForm({ ...editForm, state: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Residential Address</label>
                    <textarea value={editForm.address} rows={2}
                      onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                </div>
              </div>

              {/* Professional Info */}
              <div>
                <h3 className="text-sm font-bold text-gray-700 mb-3 border-b pb-1">Professional Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">SURCON Number</label>
                    <input type="text" value={editForm.surcon_number}
                      onChange={(e) => setEditForm({ ...editForm, surcon_number: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Specialization</label>
                    <input type="text" value={editForm.specialization}
                      onChange={(e) => setEditForm({ ...editForm, specialization: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Firm / Organisation Name</label>
                    <input type="text" value={editForm.firm_name}
                      onChange={(e) => setEditForm({ ...editForm, firm_name: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Employment Category</label>
                    <select value={editForm.employment_category}
                      onChange={(e) => setEditForm({ ...editForm, employment_category: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm">
                      <option value="">Select Employment Category</option>
                      <option value="private_sector">Private Sector</option>
                      <option value="public_sector">Public Sector</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Year Registered</label>
                    <input type="number" value={editForm.year_registered}
                      onChange={(e) => setEditForm({ ...editForm, year_registered: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Office Address</label>
                    <textarea value={editForm.firm_address} rows={2}
                      onChange={(e) => setEditForm({ ...editForm, firm_address: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Academic Qualification(s)</label>
                    <textarea value={editForm.academic_qualification} rows={2}
                      onChange={(e) => setEditForm({ ...editForm, academic_qualification: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Date Joined Branch</label>
                    <input type="date" value={editForm.date_joined_branch}
                      onChange={(e) => setEditForm({ ...editForm, date_joined_branch: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                  <div className="flex items-center pt-5">
                    <label className="flex items-center gap-2 cursor-pointer text-sm">
                      <input type="checkbox" checked={editForm.registered_with_nis_hq}
                        onChange={(e) => setEditForm({ ...editForm, registered_with_nis_hq: e.target.checked })}
                        className="w-4 h-4 text-[#0B5D1E] border-gray-300 rounded focus:ring-[#0B5D1E]" />
                      Registered with NIS Headquarters
                    </label>
                  </div>
                </div>
              </div>

              {/* Bio */}
              <div>
                <h3 className="text-sm font-bold text-gray-700 mb-3 border-b pb-1">Bio</h3>
                <textarea value={editForm.bio} rows={3}
                  onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm"
                  placeholder="Short biography..." />
              </div>

              {/* Reset Password */}
              <div>
                <h3 className="text-sm font-bold text-gray-700 mb-3 border-b pb-1">Reset Password</h3>
                <div className="flex items-end gap-3">
                  <div className="flex-1">
                    <label className="block text-xs font-semibold text-gray-500 mb-1">New Password</label>
                    <PasswordInput value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Min. 8 characters"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                  <button type="button" disabled={saving || newPassword.length < 8}
                    onClick={async () => {
                      setSaving(true);
                      try {
                        await api.post(`/accounts/admin/members/${editMember.id}/reset-password/`, { new_password: newPassword });
                        toast.success('Password reset successfully!');
                        setNewPassword('');
                      } catch {
                        toast.error('Failed to reset password.');
                      } finally {
                        setSaving(false);
                      }
                    }}
                    className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition text-sm font-semibold disabled:opacity-50 whitespace-nowrap">
                    Reset Password
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1">This will immediately change the member's login password.</p>
              </div>

            </div>
            <div className="p-6 border-t flex justify-end gap-3">
              <button onClick={() => setEditMember(null)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition">
                Cancel
              </button>
              <button onClick={handleEditSave} disabled={saving}
                className="px-4 py-2 bg-[#0B5D1E] text-white rounded-lg hover:bg-[#062E10] transition disabled:opacity-50">
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ——— Bulk Delete Confirmation Modal ——— */}
      {bulkDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h2 className="text-lg font-bold text-gray-900">Delete {selectedIds.size} Member{selectedIds.size > 1 ? 's' : ''}</h2>
              </div>
              <p className="text-sm text-gray-600">
                Are you sure you want to delete <strong>{selectedIds.size} selected member{selectedIds.size > 1 ? 's' : ''}</strong>? This action cannot be undone and will permanently remove their accounts and all associated data.
              </p>
            </div>
            <div className="p-6 border-t flex justify-end gap-3">
              <button onClick={() => setBulkDeleteConfirm(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition">
                Cancel
              </button>
              <button onClick={handleBulkDelete} disabled={saving}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50">
                {saving ? 'Deleting...' : `Delete ${selectedIds.size} Member${selectedIds.size > 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ——— Delete Confirmation Modal ——— */}
      {deleteMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h2 className="text-lg font-bold text-gray-900">Delete Member</h2>
              </div>
              <p className="text-sm text-gray-600">
                Are you sure you want to delete <strong>{deleteMember.full_name}</strong>? This action cannot be undone and will permanently remove their account and all associated data.
              </p>
            </div>
            <div className="p-6 border-t flex justify-end gap-3">
              <button onClick={() => setDeleteMember(null)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition">
                Cancel
              </button>
              <button onClick={handleDelete} disabled={saving}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50">
                {saving ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ——— Decline Application Confirmation Modal ——— */}
      {declineTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h2 className="text-lg font-bold text-gray-900">Decline Application</h2>
              </div>
              <p className="text-sm text-gray-600">
                Are you sure you want to decline the membership application from{' '}
                <strong>{declineTarget.full_name || `${declineTarget.surname || ''} ${declineTarget.other_names || ''}`.trim()}</strong>?
                The applicant will be notified by email and their registration will be
                removed, so they can submit a new application in the future.
              </p>
            </div>
            <div className="p-6 border-t flex justify-end gap-3">
              <button onClick={() => setDeclineTarget(null)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition">
                Cancel
              </button>
              <button onClick={handleDecline} disabled={saving}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50">
                {saving ? 'Declining...' : 'Decline Application'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ——— Create Member Modal ——— */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-bold text-gray-900">Create New Member</h2>
              <button onClick={resetCreateForm} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
            </div>
            <div className="p-6 space-y-6">

              {/* Photo Upload */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-2">Passport Photograph</label>
                <div className="flex items-center gap-4">
                  {createPhotoPreview ? (
                    <div className="relative">
                      <img src={createPhotoPreview} alt="Preview" className="w-20 h-20 rounded-full object-cover border-2 border-gray-200" />
                      <button type="button" onClick={() => { setCreatePhoto(null); setCreatePhotoPreview(null); }}
                        className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition shadow-sm"
                        title="Remove photo">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center text-gray-400">
                      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                  )}
                  <div>
                    <label className="cursor-pointer inline-block px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition text-sm font-medium">
                      Choose Photo
                      <input type="file" accept="image/*" onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          if (file.size > 2 * 1024 * 1024) { toast.error('Photo must be less than 2MB'); return; }
                          setCreatePhoto(file);
                          setCreatePhotoPreview(URL.createObjectURL(file));
                        }
                      }} className="hidden" />
                    </label>
                    <p className="text-xs text-gray-400 mt-1">Max 2MB — JPG, PNG</p>
                  </div>
                </div>
              </div>

              {/* Account Info */}
              <div>
                <h3 className="text-sm font-bold text-gray-700 mb-3 border-b pb-1">Account Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">First Name <span className="text-red-500">*</span></label>
                    <input type="text" required value={createForm.first_name}
                      onChange={(e) => setCreateForm({ ...createForm, first_name: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Last Name <span className="text-red-500">*</span></label>
                    <input type="text" required value={createForm.last_name}
                      onChange={(e) => setCreateForm({ ...createForm, last_name: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Email Address <span className="text-red-500">*</span></label>
                    <input type="email" required value={createForm.email}
                      onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Password <span className="text-red-500">*</span></label>
                    <PasswordInput required value={createForm.password}
                      onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                      placeholder="Min. 8 characters"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                </div>
              </div>

              {/* Membership Info */}
              <div>
                <h3 className="text-sm font-bold text-gray-700 mb-3 border-b pb-1">Membership Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Membership Number</label>
                    <input type="text" value={createForm.membership_number}
                      onChange={(e) => setCreateForm({ ...createForm, membership_number: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Category</label>
                    <select value={createForm.membership_category}
                      onChange={(e) => setCreateForm({ ...createForm, membership_category: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm">
                      <option value="fellow">Fellow (FNIS)</option>
                      <option value="corporate">Corporate Member (MNIS)</option>
                      <option value="associate">Associate Member</option>
                      <option value="graduate">Graduate Member</option>
                      <option value="student">Student Member</option>
                      <option value="technician">Technician Member</option>
                      <option value="probationer">Probationer</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Status</label>
                    <select value={createForm.membership_status}
                      onChange={(e) => setCreateForm({ ...createForm, membership_status: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm">
                      <option value="active">Active</option>
                      <option value="pending">Pending</option>
                      <option value="inactive">Inactive</option>
                      <option value="suspended">Suspended</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Shortcode</label>
                    <input type="text" value={createForm.shortcode}
                      onChange={(e) => setCreateForm({ ...createForm, shortcode: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                </div>
              </div>

              {/* Personal Details */}
              <div>
                <h3 className="text-sm font-bold text-gray-700 mb-3 border-b pb-1">Personal Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Gender</label>
                    <select value={createForm.gender}
                      onChange={(e) => setCreateForm({ ...createForm, gender: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm">
                      <option value="">—</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Date of Birth</label>
                    <input type="date" value={createForm.date_of_birth}
                      onChange={(e) => setCreateForm({ ...createForm, date_of_birth: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Phone Number</label>
                    <input type="tel" value={createForm.phone_number}
                      onChange={(e) => setCreateForm({ ...createForm, phone_number: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">State of Origin</label>
                    <select value={createForm.state_of_origin}
                      onChange={(e) => setCreateForm({ ...createForm, state_of_origin: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm">
                      <option value="">Select State</option>
                      {NIGERIAN_STATES.map((st) => (
                        <option key={st} value={st}>{st}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">City</label>
                    <input type="text" value={createForm.city}
                      onChange={(e) => setCreateForm({ ...createForm, city: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">State</label>
                    <input type="text" value={createForm.state}
                      onChange={(e) => setCreateForm({ ...createForm, state: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Residential Address</label>
                    <textarea value={createForm.address} rows={2}
                      onChange={(e) => setCreateForm({ ...createForm, address: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                </div>
              </div>

              {/* Professional Info */}
              <div>
                <h3 className="text-sm font-bold text-gray-700 mb-3 border-b pb-1">Professional Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">SURCON Number</label>
                    <input type="text" value={createForm.surcon_number}
                      onChange={(e) => setCreateForm({ ...createForm, surcon_number: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Specialization</label>
                    <input type="text" value={createForm.specialization}
                      onChange={(e) => setCreateForm({ ...createForm, specialization: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Firm / Organisation Name</label>
                    <input type="text" value={createForm.firm_name}
                      onChange={(e) => setCreateForm({ ...createForm, firm_name: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Employment Category</label>
                    <select value={createForm.employment_category}
                      onChange={(e) => setCreateForm({ ...createForm, employment_category: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm">
                      <option value="">Select Employment Category</option>
                      <option value="private_sector">Private Sector</option>
                      <option value="public_sector">Public Sector</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Year Registered</label>
                    <input type="number" value={createForm.year_registered}
                      onChange={(e) => setCreateForm({ ...createForm, year_registered: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Office Address</label>
                    <textarea value={createForm.firm_address} rows={2}
                      onChange={(e) => setCreateForm({ ...createForm, firm_address: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Academic Qualification(s)</label>
                    <textarea value={createForm.academic_qualification} rows={2}
                      onChange={(e) => setCreateForm({ ...createForm, academic_qualification: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Date Joined Branch</label>
                    <input type="date" value={createForm.date_joined_branch}
                      onChange={(e) => setCreateForm({ ...createForm, date_joined_branch: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  </div>
                  <div className="flex items-center pt-5">
                    <label className="flex items-center gap-2 cursor-pointer text-sm">
                      <input type="checkbox" checked={createForm.registered_with_nis_hq}
                        onChange={(e) => setCreateForm({ ...createForm, registered_with_nis_hq: e.target.checked })}
                        className="w-4 h-4 text-[#0B5D1E] border-gray-300 rounded focus:ring-[#0B5D1E]" />
                      Registered with NIS Headquarters
                    </label>
                  </div>
                </div>
              </div>

              {/* Bio */}
              <div>
                <h3 className="text-sm font-bold text-gray-700 mb-3 border-b pb-1">Bio</h3>
                <textarea value={createForm.bio} rows={3}
                  onChange={(e) => setCreateForm({ ...createForm, bio: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm"
                  placeholder="Short biography..." />
              </div>

            </div>
            <div className="p-6 border-t flex justify-end gap-3">
              <button onClick={resetCreateForm}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition">
                Cancel
              </button>
              <button onClick={handleCreateMember} disabled={saving}
                className="px-4 py-2 bg-[#0B5D1E] text-white rounded-lg hover:bg-[#062E10] transition disabled:opacity-50">
                {saving ? 'Creating...' : 'Create Member'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
